import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './CalculatorEnhanced.css';

// ============================================
// CONFIGURATION - EASILY CUSTOMIZE HERE
// ============================================
const CONFIG = {
  // Feature 8: ROI Timeline Graph
  timelineGraph: {
    enabled: true,
    mode: 'basic', // 'basic' or 'advanced'
    basicYears: [1, 3, 5], // Years to show in basic mode
    advancedYears: [1, 2, 3, 5, 10], // Years to show in advanced mode
    showCumulative: true, // Show cumulative savings line
  },

  // Feature 9: Downloadable Report
  downloadReport: {
    enabled: true,
    mode: 'basic', // 'basic' or 'advanced'
    includeCharts: true, // Advanced: include timeline charts
    includeBreakdown: true, // Advanced: detailed cost breakdown
    format: 'pdf', // 'pdf' or 'text'
  },

  // Feature 10: Industry Presets
  industryPresets: {
    enabled: true,
    mode: 'basic', // 'basic' or 'advanced'
    basicPresets: ['Restaurant', 'Retail', 'Healthcare'], // Basic industries
    advancedPresets: [
      'Restaurant', 'Retail', 'Healthcare',
      'Real Estate', 'Legal', 'Automotive',
      'Insurance', 'Hospitality'
    ],
  },

  // Feature 11: Comparison Mode
  comparisonMode: {
    enabled: true,
    mode: 'basic', // 'basic' or 'advanced'
    maxScenarios: 2, // Basic: 2, Advanced: 3-4
  },

  // Feature 12: Text Input for Sliders
  textInput: {
    enabled: true,
    allowManualEntry: true,
    validateOnBlur: true,
  }
};

// Industry preset data
const INDUSTRY_PRESETS = {
  'Restaurant': { calls: 800, minutes: 5, costPerHour: 18 },
  'Retail': { calls: 1200, minutes: 8, costPerHour: 20 },
  'Healthcare': { calls: 600, minutes: 12, costPerHour: 35 },
  'Real Estate': { calls: 400, minutes: 15, costPerHour: 45 },
  'Legal': { calls: 300, minutes: 20, costPerHour: 75 },
  'Automotive': { calls: 900, minutes: 10, costPerHour: 25 },
  'Insurance': { calls: 1000, minutes: 12, costPerHour: 30 },
  'Hospitality': { calls: 1500, minutes: 6, costPerHour: 16 },
};

function CircularMeter({ value, maxValue, label, color, icon }) {
  const percentage = Math.min((value / maxValue) * 100, 100);
  const circumference = 2 * Math.PI * 90;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="circular-meter">
      <svg className="meter-svg" width="220" height="220">
        <circle className="meter-background" cx="110" cy="110" r="90" />
        <circle
          className="meter-progress"
          cx="110"
          cy="110"
          r="90"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ stroke: color }}
        />
      </svg>
      <div className="meter-content">
        <div className="meter-icon">
          {icon.startsWith('http') ? <img src={icon} alt={label} /> : <span className="meter-emoji">{icon}</span>}
        </div>
        <div className="meter-value">{value.toLocaleString('en-US')}</div>
        <div className="meter-label">{label}</div>
      </div>
    </div>
  );
}

function CalculatorEnhanced() {
  // Main scenario state
  const [callsPerMonth, setCallsPerMonth] = useState(500);
  const [minutesPerCall, setMinutesPerCall] = useState(10);
  const [costPerHour, setCostPerHour] = useState(25);

  // Feature 10: Industry preset selection
  const [selectedIndustry, setSelectedIndustry] = useState('');

  // Feature 11: Comparison mode
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [scenario2, setScenario2] = useState({
    calls: 500,
    minutes: 10,
    cost: 25
  });

  // Calculate savings
  const calculateSavings = (calls, minutes, cost) => {
    const totalMinutes = calls * minutes;
    const totalHours = totalMinutes / 60;
    const savingsPercentage = 0.7;
    const hoursSavedMonth = totalHours * savingsPercentage;
    const monthlyCost = hoursSavedMonth * cost;
    const annualCost = monthlyCost * 12;
    const aiServiceCost = 500;
    const roi = monthlyCost > 0 ? ((monthlyCost - aiServiceCost) / aiServiceCost) * 100 : 0;

    return {
      monthly: Math.round(monthlyCost),
      annual: Math.round(annualCost),
      hours: Math.round(hoursSavedMonth),
      roi: Math.round(roi)
    };
  };

  // Derived synchronously so the first client render matches the prerendered values.
  const {
    monthly: monthlySavings,
    annual: annualSavings,
    hours: hoursSaved,
    roi: roiPercentage,
  } = calculateSavings(callsPerMonth, minutesPerCall, costPerHour);

  // Feature 10: Apply industry preset
  const applyIndustryPreset = (industry) => {
    if (INDUSTRY_PRESETS[industry]) {
      const preset = INDUSTRY_PRESETS[industry];
      setCallsPerMonth(preset.calls);
      setMinutesPerCall(preset.minutes);
      setCostPerHour(preset.costPerHour);
      setSelectedIndustry(industry);
    }
  };

  // Feature 9: Download report
  const downloadReport = () => {
    const years = CONFIG.timelineGraph.mode === 'basic'
      ? CONFIG.timelineGraph.basicYears
      : CONFIG.timelineGraph.advancedYears;

    let reportContent = `
╔═══════════════════════════════════════════════════════╗
║         UBOTIKA AI SAVINGS REPORT                    ║
║         Generated: ${new Date().toLocaleDateString()}                    ║
╚═══════════════════════════════════════════════════════╝

CURRENT PARAMETERS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  Monthly Calls:        ${callsPerMonth.toLocaleString()}
  Minutes per Call:     ${minutesPerCall}
  Staff Cost/Hour:      $${costPerHour}
${selectedIndustry ? `  Industry:             ${selectedIndustry}\n` : ''}
IMMEDIATE IMPACT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  💰 Monthly Savings:   $${monthlySavings.toLocaleString()}
  📅 Annual Savings:    $${annualSavings.toLocaleString()}
  ⏰ Hours Saved/Month: ${hoursSaved}
  📈 ROI:               ${roiPercentage}%

PROJECTED SAVINGS TIMELINE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${years.map(year => {
  const yearSavings = annualSavings * year;
  const aiCost = 500 * 12 * year;
  const netSavings = yearSavings - aiCost;
  return `  Year ${year}: $${yearSavings.toLocaleString()} (Net: $${netSavings.toLocaleString()})`;
}).join('\n')}

${CONFIG.downloadReport.includeBreakdown ? `
DETAILED COST BREAKDOWN:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  Total Monthly Call Time:    ${Math.round(callsPerMonth * minutesPerCall / 60)} hours
  Time Saved (70%):           ${hoursSaved} hours
  AI Service Cost:            $500/month
  Labor Cost Saved:           $${monthlySavings.toLocaleString()}/month
  Net Monthly Benefit:        $${Math.max(0, monthlySavings - 500).toLocaleString()}
` : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Ready to transform your business with AI?
Contact us at hello@ubotika.com
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ubotika-roi-report-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Feature 8: Timeline data
  const years = CONFIG.timelineGraph.mode === 'basic'
    ? CONFIG.timelineGraph.basicYears
    : CONFIG.timelineGraph.advancedYears;

  const timelineData = years.map(year => ({
    year,
    savings: annualSavings * year,
    cost: 500 * 12 * year,
    net: (annualSavings * year) - (500 * 12 * year)
  }));

  const maxTimelineValue = Math.max(...timelineData.map(d => d.savings));

  const availablePresets = CONFIG.industryPresets.mode === 'basic'
    ? CONFIG.industryPresets.basicPresets
    : CONFIG.industryPresets.advancedPresets;

  // Scenario 2 calculations for comparison
  const scenario2Savings = comparisonEnabled
    ? calculateSavings(scenario2.calls, scenario2.minutes, scenario2.cost)
    : null;

  return (
    <section className="calculator calculator-dashboard" id="calculator">
      <div className="container">
        <motion.div
          className="calculator-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>AI Savings Dashboard</h2>
          <p className="calculator-subtitle">
            Calculate your potential savings with AI voice automation
          </p>
        </motion.div>

        {/* Feature 10: Industry Presets */}
        {CONFIG.industryPresets.enabled && (
          <motion.div
            className="industry-presets"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <label htmlFor="industry-select">Quick Start - Select Your Industry:</label>
            <select
              id="industry-select"
              value={selectedIndustry}
              onChange={(e) => applyIndustryPreset(e.target.value)}
              className="industry-select"
            >
              <option value="">Custom Configuration</option>
              {availablePresets.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </motion.div>
        )}

        {/* Feature 11: Comparison Mode Toggle */}
        {CONFIG.comparisonMode.enabled && (
          <motion.div
            className="comparison-toggle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            viewport={{ once: true }}
          >
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={comparisonEnabled}
                onChange={(e) => setComparisonEnabled(e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-slider"></span>
              <span className="toggle-text">Compare 2 Scenarios</span>
            </label>
          </motion.div>
        )}

        <div className={`calculator-grid ${comparisonEnabled ? 'comparison-mode' : ''}`}>
          {/* Scenario 1 */}
          <motion.div
            className="calculator-controls"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {comparisonEnabled && <h3 className="scenario-title">Scenario 1</h3>}

            {/* Feature 12: Slider with Text Input */}
            <div className="control-group">
              <div className="control-header">
                <label><span className="control-icon">📞</span> Monthly Calls</label>
                {CONFIG.textInput.enabled ? (
                  <input
                    type="number"
                    min="0"
                    max="5000"
                    value={callsPerMonth}
                    onChange={(e) => setCallsPerMonth(Math.min(5000, Math.max(0, Number(e.target.value) || 0)))}
                    className="control-value-input"
                  />
                ) : (
                  <span className="control-value">{callsPerMonth}</span>
                )}
              </div>
              <input
                type="range"
                min="0"
                max="5000"
                value={callsPerMonth}
                onChange={(e) => setCallsPerMonth(Number(e.target.value))}
                className="dashboard-slider"
              />
              <div className="control-range">
                <span>0</span>
                <span>5,000</span>
              </div>
            </div>

            <div className="control-group">
              <div className="control-header">
                <label><span className="control-icon">⏱️</span> Minutes per Call</label>
                {CONFIG.textInput.enabled ? (
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={minutesPerCall}
                    onChange={(e) => setMinutesPerCall(Math.min(60, Math.max(1, Number(e.target.value) || 1)))}
                    className="control-value-input"
                  />
                ) : (
                  <span className="control-value">{minutesPerCall} min</span>
                )}
              </div>
              <input
                type="range"
                min="1"
                max="60"
                value={minutesPerCall}
                onChange={(e) => setMinutesPerCall(Number(e.target.value))}
                className="dashboard-slider"
              />
              <div className="control-range">
                <span>1</span>
                <span>60</span>
              </div>
            </div>

            <div className="control-group">
              <div className="control-header">
                <label><span className="control-icon">💰</span> Staff Cost per Hour</label>
                {CONFIG.textInput.enabled ? (
                  <input
                    type="number"
                    min="10"
                    max="100"
                    value={costPerHour}
                    onChange={(e) => setCostPerHour(Math.min(100, Math.max(10, Number(e.target.value) || 10)))}
                    className="control-value-input"
                  />
                ) : (
                  <span className="control-value">${costPerHour}</span>
                )}
              </div>
              <input
                type="range"
                min="10"
                max="100"
                value={costPerHour}
                onChange={(e) => setCostPerHour(Number(e.target.value))}
                className="dashboard-slider"
              />
              <div className="control-range">
                <span>$10</span>
                <span>$100</span>
              </div>
            </div>
          </motion.div>

          {/* Scenario 2 - Comparison Mode */}
          {comparisonEnabled && CONFIG.comparisonMode.enabled && (
            <motion.div
              className="calculator-controls scenario-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="scenario-title">Scenario 2</h3>

              <div className="control-group">
                <div className="control-header">
                  <label><span className="control-icon">📞</span> Monthly Calls</label>
                  <input
                    type="number"
                    min="0"
                    max="5000"
                    value={scenario2.calls}
                    onChange={(e) => setScenario2({...scenario2, calls: Math.min(5000, Math.max(0, Number(e.target.value) || 0))})}
                    className="control-value-input"
                  />
                </div>
                <input
                  type="range"
                  min="0"
                  max="5000"
                  value={scenario2.calls}
                  onChange={(e) => setScenario2({...scenario2, calls: Number(e.target.value)})}
                  className="dashboard-slider"
                />
                <div className="control-range">
                  <span>0</span>
                  <span>5,000</span>
                </div>
              </div>

              <div className="control-group">
                <div className="control-header">
                  <label><span className="control-icon">⏱️</span> Minutes per Call</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={scenario2.minutes}
                    onChange={(e) => setScenario2({...scenario2, minutes: Math.min(60, Math.max(1, Number(e.target.value) || 1))})}
                    className="control-value-input"
                  />
                </div>
                <input
                  type="range"
                  min="1"
                  max="60"
                  value={scenario2.minutes}
                  onChange={(e) => setScenario2({...scenario2, minutes: Number(e.target.value)})}
                  className="dashboard-slider"
                />
                <div className="control-range">
                  <span>1</span>
                  <span>60</span>
                </div>
              </div>

              <div className="control-group">
                <div className="control-header">
                  <label><span className="control-icon">💰</span> Staff Cost per Hour</label>
                  <input
                    type="number"
                    min="10"
                    max="100"
                    value={scenario2.cost}
                    onChange={(e) => setScenario2({...scenario2, cost: Math.min(100, Math.max(10, Number(e.target.value) || 10))})}
                    className="control-value-input"
                  />
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={scenario2.cost}
                  onChange={(e) => setScenario2({...scenario2, cost: Number(e.target.value)})}
                  className="dashboard-slider"
                />
                <div className="control-range">
                  <span>$10</span>
                  <span>$100</span>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Meters */}
        <motion.div
          className="dashboard-meters"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <CircularMeter
            value={monthlySavings}
            maxValue={10000}
            label="Monthly Savings"
            color="#ff8a3d"
            icon="https://storage.googleapis.com/lupatest/Ubotika-nav/Msavings.jpg"
          />
          <CircularMeter
            value={annualSavings}
            maxValue={120000}
            label="Annual Savings"
            color="#ff8a3d"
            icon="https://storage.googleapis.com/lupatest/Ubotika-nav/Asavings.jpg"
          />
          <CircularMeter
            value={hoursSaved}
            maxValue={500}
            label="Hours Saved"
            color="#ffa65e"
            icon="https://storage.googleapis.com/lupatest/Ubotika-nav/hours.jpg"
          />
          <CircularMeter
            value={roiPercentage}
            maxValue={1000}
            label="ROI %"
            color="#f59e0b"
            icon="https://storage.googleapis.com/lupatest/Ubotika-nav/roi.jpg"
          />
        </motion.div>

        {/* Feature 8: ROI Timeline Graph */}
        {CONFIG.timelineGraph.enabled && (
          <motion.div
            className="timeline-graph"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="timeline-title">
              <span className="header-icon">📈</span>
              Projected Savings Timeline
            </h3>
            <div className="timeline-bars">
              {timelineData.map((data, index) => (
                <div key={data.year} className="timeline-bar-container">
                  <div className="timeline-bar-wrapper">
                    <div
                      className="timeline-bar"
                      style={{
                        height: `${(data.savings / maxTimelineValue) * 200}px`,
                        animationDelay: `${index * 0.1}s`
                      }}
                    >
                      <div className="timeline-bar-value">${(data.savings / 1000).toFixed(0)}K</div>
                    </div>
                  </div>
                  <div className="timeline-bar-label">Year {data.year}</div>
                  <div className="timeline-bar-sublabel">
                    Net: ${(data.net / 1000).toFixed(0)}K
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Comparison Results */}
        {comparisonEnabled && scenario2Savings && (
          <motion.div
            className="comparison-results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <h3 className="comparison-title">
              <span className="header-icon">⚖️</span>
              Scenario Comparison
            </h3>
            <div className="comparison-grid">
              <div className="comparison-metric">
                <div className="metric-label">Monthly Savings Difference</div>
                <div className="metric-value" style={{ color: monthlySavings >= scenario2Savings.monthly ? '#ff8a3d' : '#f59e0b' }}>
                  ${Math.abs(monthlySavings - scenario2Savings.monthly).toLocaleString()}
                  <span className="metric-arrow">{monthlySavings >= scenario2Savings.monthly ? '↑' : '↓'}</span>
                </div>
              </div>
              <div className="comparison-metric">
                <div className="metric-label">Annual Savings Difference</div>
                <div className="metric-value" style={{ color: annualSavings >= scenario2Savings.annual ? '#ff8a3d' : '#f59e0b' }}>
                  ${Math.abs(annualSavings - scenario2Savings.annual).toLocaleString()}
                  <span className="metric-arrow">{annualSavings >= scenario2Savings.annual ? '↑' : '↓'}</span>
                </div>
              </div>
              <div className="comparison-metric">
                <div className="metric-label">Better Scenario</div>
                <div className="metric-value">
                  {monthlySavings >= scenario2Savings.monthly ? 'Scenario 1' : 'Scenario 2'}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Summary */}
        <motion.div
          className="dashboard-summary"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="summary-card">
            <h3><span className="header-icon">💡</span> Your Potential Impact</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Annual ROI</span>
                <span className="summary-value">{roiPercentage > 0 ? roiPercentage : 0}%</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Payback Period</span>
                <span className="summary-value">{monthlySavings > 500 ? '< 1 month' : 'N/A'}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Time Efficiency</span>
                <span className="summary-value">70%</span>
              </div>
            </div>

            {/* Feature 9: Download Report Button */}
            {CONFIG.downloadReport.enabled && (
              <button
                onClick={downloadReport}
                className="download-report-btn"
              >
                <span className="btn-icon">📄</span>
                Download Full Report
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default CalculatorEnhanced;
